import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, Calendar, MessageCircle } from 'lucide-react';
import { PropertyUnit } from '@/lib/units';
import { trackEvent } from '@/lib/analytics';

interface UnitCTAProps {
  unit: PropertyUnit;
}

export const UnitCTA = ({ unit }: UnitCTAProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show CTA when user has scrolled past 50% of the page
      setIsVisible(scrollPosition > (documentHeight - windowHeight) * 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePhoneClick = () => {
    trackEvent('unit_cta_phone', { unit_number: unit.unit_number });
    window.open('tel:+13451234567');
  };

  const handleEmailClick = () => {
    trackEvent('unit_cta_email', { unit_number: unit.unit_number });
    window.open(`mailto:info@thepearl.ky?subject=Interest in Unit ${unit.unit_number}`);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      `Hi! I'm interested in Unit ${unit.unit_number} at The Pearl at Grand Harbour. Can you provide more information?`
    );
    trackEvent('unit_cta_whatsapp', { unit_number: unit.unit_number });
    window.open(`https://wa.me/13451234567?text=${message}`, '_blank');
  };

  const handleScheduleClick = () => {
    trackEvent('unit_cta_schedule', { unit_number: unit.unit_number });
    // In production, this would open HubSpot Meetings or similar
    console.log('Schedule consultation for Unit', unit.unit_number);
  };

  return (
    <>
      {/* Desktop Floating CTA */}
      <div className="hidden lg:block">
        <Card className={`fixed bottom-[80px] right-6 z-40 transition-all duration-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 min-w-[200px]">
              <div className="text-center mb-2">
                <h3 className="font-semibold text-sm text-foreground">Interested?</h3>
                <p className="text-xs text-muted-foreground">Get in touch today</p>
              </div>
              
              <Button size="sm" onClick={handlePhoneClick} className="gap-2">
                <Phone className="w-4 h-4" />
                Call Now
              </Button>
              
              <Button variant="outline" size="sm" onClick={handleEmailClick} className="gap-2">
                <Mail className="w-4 h-4" />
                Email Us
              </Button>
              
              <Button variant="outline" size="sm" onClick={handleWhatsAppClick} className="gap-2">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden">
        <div className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}>
          <Card className="rounded-none border-t">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Button 
                  size="sm" 
                  onClick={handlePhoneClick} 
                  className="flex-1 gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleEmailClick} 
                  className="flex-1 gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleWhatsAppClick} 
                  className="flex-1 gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

